'use strict';

const Vuex = require('vuex');
const auth = require('../../../services/client/auth');
const isProd = require('../../../services/client/is-production')();
const { getWishlistedProducts } = require('../../../services/client/voxstar');
const { getPageUri } = require('../../../services/client/page');
const { buildSearchParams, processProducts } = require('../lib/products');
const analytics = require('../lib/analytics');
const sailthru = require('../lib/sailthru');
const { processSuggestions } = require('../lib/utils');
const { writeFiltersToUrl } = require('../lib/url-filters');

const SEARCH_TIMEOUT_MS = 60000;
const PRODUCT_SEARCH_PATH = '/_voxstar/strat-search';

/**
 * Syncs the current search term and product filters into the URL via
 * history.replaceState, so the address bar reflects a shareable link for
 * the current result set. Uses replaceState (not pushState) to avoid
 * stacking history entries the SPA can't meaningfully navigate between.
 * @param {string} term - The current search term.
 * @param {Object} filters - The current productFilters state.
 * @returns {void}
 */
function syncUrlToState(term, filters) {
  const url = new URL(window.location.href);

  if (term) {
    url.searchParams.set('q', term);
  } else {
    url.searchParams.delete('q');
  }

  writeFiltersToUrl(url, filters);
  history.replaceState(null, '', url.toString());
}

/**
 * Initializes the Vuex store for the search component.
 * @param {Object} data - Initial state data.
 * @returns {Vuex.Store} - The initialized Vuex store.
 */
function initStore(data = {}) {
  // Tracks the in-flight search request so a newer one can cancel it
  // and stale responses can be ignored. Lives in the closure (not in
  // reactive state) since templates never need to read it.
  let currentController = null;

  function startRequest() {
    if (currentController) {
      currentController.abort();
    }
    const controller = new AbortController();

    currentController = controller;
    const timer = setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS);

    return {
      controller,
      isCurrent: () => currentController === controller,
      finish: () => {
        clearTimeout(timer);
        if (currentController === controller) {
          currentController = null;
        }
      },
    };
  }

  return new Vuex.Store({
    // Do not enable strict mode on prod because of performance reasons:
    // https://v3.vuex.vuejs.org/guide/strict.html#development-vs-production
    strict: !isProd,
    state: {
      // data coming from clay
      disclaimerText: data.disclaimerText || '',
      popularSearches: data.popularSearches || [],
      searchSuggestions: data.suggestions ? processSuggestions(data.suggestions) : [],
      // common
      loading: false,
      searchValue: '',
      activeTab: 'top-results', // supports top-results, products, and stories
      isStickyOnMobile: false, // true when .search-wrapper has reached the viewport top
      noResults: false, // no results section if both articles and products are empty
      showPriceRange: false,
      // for products
      productResults: [],
      productFilters: {
        priceInit: {
          min: null,
          max: null,
        },
        price: {
          min: null,
          max: null,
        },
        onSale: false,
        tags: [],
      },
      savedProductIDs: [],
      savedForLaterLink: data.savedForLaterLink || '',
      pageUri: getPageUri(),
      // Attributes the first search after page load back to the referring page
      // (article, homepage, etc.) via itm_source. Reset to the default once the
      // user submits a follow-up/different search — see submitSearch.
      position: analytics.getPosition(),
      // for articles
      storyResults: [],
      storiesLimit: 50,
      storiesTotal: 100,
      errorMsg: '',
      errorMsgTimeout: { id: null },
      showErrorMsg: false,
      // Last term a response landed for, regardless of whether it had results.
      // Used to detect a genuinely new search term (e.g. for position reset).
      // Distinct from lastSurveyTerm below, which only advances on terms that
      // returned products and drives the survey-specific re-prompt logic.
      lastSearchTerm: '',
      // for survey
      showSurvey: false,
      lastSurveyTerm: '',
    },
    getters: {
      productResults: (state) => state.productResults,
      productFilters: (state) => state.productFilters,
      storyResults: (state) => state.storyResults,
    },
    actions: {
      setSearchValue({ commit }, val) {
        commit('setSearchValue', val);
      },
      setActiveTab({ commit }, val) {
        commit('setActiveTab', val);
      },
      showLoading({ commit }) {
        commit('showLoading');
      },
      hideLoading({ commit }) {
        commit('hideLoading');
      },
      showNoResults({ commit }) {
        commit('showNoResults');
      },
      hideNoResults({ commit }) {
        commit('hideNoResults');
      },
      updatePriceMax({ commit }, val) {
        commit('updatePriceMax', val);
      },
      updatePriceMin({ commit }, val) {
        commit('updatePriceMin', val);
      },
      updateTags({ commit }, val) {
        commit('updateTags', val);
      },
      updateOnSale({ commit }, val) {
        commit('updateOnSale', val);
      },
      submitSearch({ commit, state }, { term, initialFilters = null } = {}) {
        commit('setSearchValue', term);

        // initialFilters seeds filters parsed from the URL on the first load
        // of a shareable link. A normal user-typed search (no initialFilters)
        // still resets filters, since a new search term should not carry
        // over filters from a previous one.
        if (initialFilters) {
          commit('updatePriceMin', initialFilters.price.min);
          commit('updatePriceMax', initialFilters.price.max);
          commit('updateOnSale', initialFilters.onSale);
          commit('updateTags', initialFilters.tags);

          if (initialFilters.price.min !== null || initialFilters.price.max !== null) {
            commit('setShowPriceRange', true);
          }
        } else {
          commit('resetFilters');
        }

        // Keep the URL in sync with the current search term and filters so
        // the result set is shareable.
        syncUrlToState(term, state.productFilters);

        if (!term) return;

        commit('hideNoResults');
        commit('showLoading');
        // Dismiss any visible survey immediately so stale feedback isn't collected
        commit('setShowSurvey', false);
        analytics.resetImpressions();

        const params = buildSearchParams(term, state.productFilters, false);
        const req = startRequest();

        return fetch(`${PRODUCT_SEARCH_PATH}?${params}`, {
          signal: req.controller.signal,
        })
          .then((resp) => resp.json())
          .then(({ data }) => {
            if (!req.isCurrent()) return;

            if (!data) {
              commit('showNoResults');
              commit('setLastSearchTerm', term);
              return;
            }

            const { locals, ref: uriRef } = window.searchRemix;
            const articles = data.articles || [];
            const products = data.products || [];
            const metadata = data.metadata || { maxPrice: 0, minPrice: 0 };

            if (articles.length === 0 && products.length === 0) {
              commit('showNoResults');
              commit('setLastSearchTerm', term);
              return;
            }

            const processedProducts = processProducts(products, locals, uriRef);
            const isNewSearchTerm = term !== state.lastSearchTerm;
            const isNewSurveyTerm = term !== state.lastSurveyTerm;

            commit('updatePriceInit', metadata);
            commit('setProductResults', processedProducts);
            commit('setStoryResults', articles);
            commit('hideNoResults');

            if (state.lastSearchTerm && isNewSearchTerm)
              commit('setPosition', analytics.DEFAULT_POSITION);
            // fire search event
            analytics.fireSearchEvent(term, state.productFilters, state.position);

            commit('setLastSearchTerm', term);

            // Show survey 5s after results land, but only once per unique search term
            // so repeated searches and back-navigations don't re-prompt the user.
            if (processedProducts.length && isNewSurveyTerm) {
              commit('setLastSurveyTerm', term);
              setTimeout(() => commit('setShowSurvey', true), 5000);
            }
          })
          .catch((err) => {
            if (!req.isCurrent()) return; // superseded by a newer request
            if (err.name !== 'AbortError') {
              console.error('[search-remix] submitSearch failed:', err);
            }
            commit('setProductResults', []);
            commit('setStoryResults', []);
            commit('showNoResults');
          })
          .finally(() => {
            const wasCurrent = req.isCurrent();

            req.finish();
            if (wasCurrent) commit('hideLoading');
          });
      },
      applyFilters({ commit, state }) {
        const { searchValue, productFilters } = state;

        if (!searchValue) return;

        syncUrlToState(searchValue, productFilters);

        commit('showLoading');
        analytics.resetImpressions();

        const params = buildSearchParams(searchValue, productFilters, true);
        const req = startRequest();

        return fetch(`${PRODUCT_SEARCH_PATH}?${params}`, {
          signal: req.controller.signal,
        })
          .then((resp) => resp.json())
          .then(({ data }) => {
            if (!req.isCurrent()) return;

            const { locals, ref: uriRef } = window.searchRemix;
            const products = data ? data.products || [] : [];
            const processedProducts = processProducts(products, locals, uriRef);

            commit('setProductResults', processedProducts);
            // Filter changes don't reset position — only a new/different submitSearch does.
            analytics.fireSearchEvent(state.searchValue, state.productFilters, state.position);
          })
          .catch((err) => {
            if (!req.isCurrent()) return; // superseded
            if (err.name !== 'AbortError') {
              console.error('[search-remix] applyFilters failed:', err);
            }
            // Keep prior product results on filter failure rather than blanking
            // them — the user still has a valid result set from the last search.
          })
          .finally(() => {
            const wasCurrent = req.isCurrent();

            req.finish();
            if (wasCurrent) commit('hideLoading');
          });
      },
      clearFiltersAndSubmitSearch({ dispatch, state }) {
        const term = state.searchValue || '';
        const filters = state.productFilters;

        syncUrlToState(term, filters);

        dispatch('submitSearch', { term });
      },
      showErrorMsg({ commit }, { message = '', duration = 5 }) {
        commit('showErrorMsg', { message, duration });
      },
      hideErrorMsg({ commit }) {
        commit('hideErrorMsg');
      },
      loadSavedProducts({ commit }) {
        auth.onReady((profile) => {
          if (!profile.isAuthenticated) return;

          getWishlistedProducts()
            .then((res) => {
              if (res.status !== 200) return;

              const items = res?.data?.items || [];
              const ids = items.map((item) => item.id);

              commit('setSavedProductIDs', ids);
            })
            .catch((err) => {
              console.error('[search-remix] loadSavedProducts failed:', err);
            });
        });
      },
      autoOptInUserOnPostAuth() {
        auth.onReady((profile) => {
          sailthru.autoOptInUserOnPostSavedAuth(profile);
        });
      },
    },
    mutations: {
      setSearchValue(state, val) {
        state.searchValue = val;
      },
      setActiveTab(state, val) {
        state.activeTab = val;
      },
      showLoading(state) {
        state.loading = true;
      },
      hideLoading(state) {
        state.loading = false;
      },
      showNoResults(state) {
        state.noResults = true;
      },
      hideNoResults(state) {
        state.noResults = false;
      },
      showAllStories(state) {
        state.storiesLimit = state.storiesTotal;
      },
      updatePriceMax(state, val) {
        state.productFilters.price.max = val;
      },
      updatePriceMin(state, val) {
        state.productFilters.price.min = val;
      },
      updatePriceInit(state, { minPrice, maxPrice }) {
        const roundedMin = Math.floor(minPrice / 5) * 5;
        const roundedMax = Math.ceil(maxPrice / 5) * 5;
        const { min: activeMin, max: activeMax } = state.productFilters.price;

        // priceInit is the price-range slider's absolute bounds. It must never
        // end up narrower than a currently active price filter (e.g. one seeded
        // from a shared URL) — the response metadata reflects the matching
        // result set, which can be tighter than the requested filter bounds.
        // If priceInit were narrower than price.min/max, ProductFilters.vue's
        // minPercent/maxPercent would compute outside 0-100 and the fill would
        // render past the track edges.
        state.productFilters.priceInit.min =
          activeMin !== null ? Math.min(roundedMin, activeMin) : roundedMin;
        state.productFilters.priceInit.max =
          activeMax !== null ? Math.max(roundedMax, activeMax) : roundedMax;
      },
      updateTags(state, val) {
        state.productFilters.tags = val;
      },
      updateOnSale(state, val) {
        state.productFilters.onSale = val;
      },
      setProductResults(state, products) {
        state.productResults = products;
      },
      setStoryResults(state, articles) {
        state.storyResults = articles;
      },
      togglePriceRange(state) {
        state.showPriceRange = !state.showPriceRange;
      },
      setShowPriceRange(state, val) {
        state.showPriceRange = val;
      },
      resetFilters(state) {
        state.productFilters.price.min = null;
        state.productFilters.price.max = null;
        state.productFilters.onSale = false;
        state.productFilters.tags = [];
      },
      showErrorMsg(state, { message = '', duration = 5 }) {
        state.errorMsg = message;
        state.errorMsgTimeout.id = setTimeout(
          () => this.commit('hideErrorMsg'),
          duration * 1000
        );
        state.showErrorMsg = true;
      },
      hideErrorMsg(state) {
        if (state.errorMsgTimeout.id) clearTimeout(state.errorMsgTimeout.id);
        state.showErrorMsg = false;
        state.errorMsg = '';
      },
      setStickyOnMobile(state, val) {
        state.isStickyOnMobile = val;
      },
      setSavedProductIDs(state, ids) {
        state.savedProductIDs = ids;
      },
      addSavedProductID(state, id) {
        state.savedProductIDs.push(id);
      },
      removeSavedProductID(state, id) {
        state.savedProductIDs = state.savedProductIDs.filter((x) => x !== id);
      },
      setShowSurvey(state, val) {
        state.showSurvey = val;
      },
      setLastSearchTerm(state, term) {
        state.lastSearchTerm = term;
      },
      setLastSurveyTerm(state, term) {
        state.lastSurveyTerm = term;
      },
      setPosition(state, val) {
        state.position = val;
      },
    },
  });
}

module.exports = {
  initStore,
};
